import { Html } from "@react-three/drei";
import type { SensorInstall } from "../../stores/layoutStore";
import type { SensorReading } from "../../stores/telemetryStore";
import { tokens } from "../../styles/tokens";
import { LABEL_Z_RANGE } from "../labelZ";

const HEALTH_COLOR: Record<SensorReading["health"], string> = {
  ok:      tokens.semantic.success,
  warn:    tokens.semantic.warn,
  fail:    tokens.semantic.danger,
  offline: tokens.ink[500],
};

const HEALTH_RANK: Record<SensorReading["health"], number> = {
  fail: 3, offline: 2, warn: 1, ok: 0,
};

interface Cluster {
  cx: number;
  cz: number;
  count: number;
  worstHealth: SensorReading["health"];
}

interface Props {
  sensors: SensorInstall[];
  readings: Record<string, SensorReading>;
  cellSize?: number;
}

export function SensorClusterBillboard({ sensors, readings, cellSize = 10 }: Props) {
  const cellMap = new Map<string, SensorInstall[]>();
  for (const s of sensors) {
    const key = `${Math.floor(s.pos.x / cellSize)}:${Math.floor(s.pos.z / cellSize)}`;
    if (!cellMap.has(key)) cellMap.set(key, []);
    cellMap.get(key)!.push(s);
  }

  const clusters: Cluster[] = [];
  for (const [, group] of cellMap) {
    const cx = group.reduce((a, s) => a + s.pos.x, 0) / group.length;
    const cz = group.reduce((a, s) => a + s.pos.z, 0) / group.length;
    let worst: SensorReading["health"] = "ok";
    for (const s of group) {
      const h = readings[s.id]?.health ?? "offline";
      if (HEALTH_RANK[h] > HEALTH_RANK[worst]) worst = h;
    }
    clusters.push({ cx, cz, count: group.length, worstHealth: worst });
  }

  return (
    <>
      {clusters.map((c, i) => (
        <Html key={i} position={[c.cx, 3, c.cz]} center occlude={false} zIndexRange={LABEL_Z_RANGE}>
          <div style={{
            background: "rgba(5,18,46,0.82)",
            border: `1px solid ${HEALTH_COLOR[c.worstHealth]}`,
            borderRadius: 12,
            padding: "3px 9px",
            fontSize: 11,
            fontFamily: tokens.font.mono,
            color: HEALTH_COLOR[c.worstHealth],
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}>
            ⬡ {c.count}
          </div>
        </Html>
      ))}
    </>
  );
}
